/**
 * Country-specific labels for Quote Templates (Preview and DPR)
 * 
 * This file contains all labels and translations used in quote templates,
 * organized by country. Based on the Japan template structure.
 * 
 * Usage:
 * import { getQuotePreviewLabels, getDPRProformaLabels } from './QuoteTemplateLabels';
 * const labels = getQuotePreviewLabels('Japan');
 */

export interface QuotePreviewLabels {
  quotation: string;
  microsoft: string;
  quoteDetails: {
    quotationId: string;
    effectiveFrom: string;
    effectiveTo: string;
    quotationDate: string;
    expiryDate: string;
  };
  customerInfo: {
    companyName: string;
    accountNumber: string;
    customerTaxId: string;
    contactName: string;
    contactEmail: string;
    phone: string;
    customerPo: string;
    opportunityId: string;
  };
  microsoftAccount: {
    accountLead: string;
    accountLeadEmail: string;
    accountLeadPhone: string;
  };
  addresses: {
    billTo: string;
    shipTo: string;
  };
  productTable: {
    itemNumber: string;
    description: string;
    partNumber: string;
    quantity: string;
    Price: string;
    discountAmount: string;
    discountPercentage: string;
    unitPrice: string;
    lineTotal: string;
    tax: string;
    fee: string;
  };
  summary: {
    subtotal: string;
    subtotalWithLevy: string;
    totalDiscount: string;
    totalTax: string;
    totalFee: string;
    grandTotal: string;
    currency: string;
    shipping: string;
  };
  payment: {
    paymentTerms: string;
    paymentMethod: string;
  };
  footer: {
    termsConditions: string;
    thankYou: string;
    validityNote: string;
    contactNote: string;
    customerSignature: string;
    date: string;
    termsNote: string;
    learnMore: string;
    levyFeeExplanation: string;
    copyrightLevyFeeLabel: string;
    officialNoticeLabel: string;
    reimbursementLabel: string;
  };
  buttons: {
    generatePdf: string;
    back: string;
    print: string;
  };
}

export interface DPRProformaLabels {
  downpaymentRequest: string;
  microsoft: string;
  dprDetails: {
    pageNumber: string;
    of: string;
    paymentReference: string;
    dueDate: string;
    requestNumber: string;
    requestDate: string;
    quotationNumber: string;
    vatRegistration: string;
  };
  customerInfo: {
    billTo: string;
    shipTo: string;
    companyName: string;
    businessPartner: string;
    customerNumber: string;
    customerGstId: string;
    customerPo: string;
    customer: string;
    customerTaxId: string;
    soldToId: string;
  };
  productTable: {
    itemNumber: string;
    description: string;
    quantity: string;
    Price: string;
    msrp: string;
    extendedLineTax: string;
    extendedCopyrightLevyFee: string;
    lineAmount: string;
    unitPrice: string;
    tax: string;
  };
  summary: {
    subtotal: string;
    subtotalWithLevy: string;
    gst: string;
    totalTax: string;
    grandTotal: string;
    downpaymentAmount: string;
    remainingAmount: string;
    copyrightLevyFee: string;
  };
  payment: {
    paymentTerms: string;
    paymentMethod: string;
    bankDetails: string;
    wireTransfer: string;
    wireTransferOption: string;
    bankName: string;
    bankAddress: string;
    bankPhone: string;
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    accountName: string;
    accountAddress: string;
    accountNumber: string;
    ibanNumber: string;
    swiftCode: string;
    swiftBicCode: string;
    switchCode: string;
    sortCode: string;
    correspondingBank: string;
    vat: string;
    correspondingBankSwiftBicCode: string;
    bankKey: string;
    bcCode: string;
    currencyCode: string;
    companyCode: string;
    remittanceNote: string;
  };
  footer: {
    comments: string;
    paymentAppreciation: string;
    creditCardTitle: string;
    creditCardInstructions: string;
    termsConditions: string;
    termsInstructionsWireTransfer: string;
    termsInstructionsCreditCard: string;
    supportEmail: string;
  };
  buttons: {
    downloadPdf: string;
    print: string;
    back: string;
  };
}

export interface CountryTemplateLabels {
  quotePreview: QuotePreviewLabels;
  dprProforma: DPRProformaLabels;
}

// Japan Labels (Japanese language) - Based on existing Japan template
const JapanLabels: CountryTemplateLabels = {
  quotePreview: {
    quotation: "見積書",
    microsoft: "Microsoft",
    quoteDetails: {
      quotationId: "お見積番号",
      effectiveFrom: "発行日",
      effectiveTo: "有効期限",
      quotationDate: "見積書日付",
      expiryDate: "有効期限"
    },
    customerInfo: {
      companyName: "会社名",
      accountNumber: "お客様 ID",
      customerTaxId: "消費税登録番号",
      contactName: "連絡先名",
      contactEmail: "連絡先メール",
      phone: "電話番号",
      customerPo: "お客様注文番号",
      opportunityId: "Opportunity ID"
    },
    microsoftAccount: {
      accountLead: "マイクロソフト担当者",
      accountLeadEmail: "担当者メール",
      accountLeadPhone: "担当者電話番号"
    },
    addresses: {
      billTo: "請求先住所",
      shipTo: "配送先住所"
    },
    productTable: {
      itemNumber: "品目番号",
      description: "製品名",
      partNumber: "型番",
      quantity: "数量",
      Price: "価格",
      discountAmount: "割引額",
      discountPercentage: "割引率",
      unitPrice: "単価",
      lineTotal: "合計",
      tax: "消費税額",
      fee: "手数料"
    },
    summary: {
      subtotal: "小計",
      subtotalWithLevy: "小計",
      totalDiscount: "総割引額",
      totalTax: "消費税合計",
      totalFee: "手数料合計",
      grandTotal: "総合計",
      currency: "通貨",
      shipping: "送料"
    },
    payment: {
      paymentTerms: "支払条件",
      paymentMethod: "支払方法"
    },
    footer: {
      termsConditions: "利用規約",
      thankYou: "お取引いただき、ありがとうございます。",
      validityNote: "この見積書は有効期限内のみ有効です。",
      contactNote: "ご質問がございましたら、担当者までお気軽にお問い合わせください。",
      customerSignature: "お客様承認サイン",
      date: "日付",
      termsNote: "見積書に添付されている条件をご確認ください。発注書の発行または購入価格のお支払いにより、製品に関してここに添付されているビジネス販売条件に同意されたものとみなされます。",
      learnMore: "詳細については、こちらをクリック",
      levyFeeExplanation: "",
      copyrightLevyFeeLabel: "",
      officialNoticeLabel: "",
      reimbursementLabel: ""
    },
    buttons: {
      generatePdf: "PDF見積書を生成",
      back: "戻る",
      print: "印刷"
    }
  },
  dprProforma: {
    downpaymentRequest: "頭金支払い請求書",
    microsoft: "Microsoft",
    dprDetails: {
      pageNumber: "ページ番号",
      of: "of",
      paymentReference: "支払い参照番号",
      dueDate: "支払期限",
      requestNumber: "リクエスト番号",
      requestDate: "発行日",
      quotationNumber: "お見積番号",
      vatRegistration: "VAT Registration"
    },
    customerInfo: {
      billTo: "請求先",
      shipTo: "配送先",
      companyName: "会社名",
      businessPartner: "ビジネスパートナー番号",
      customerNumber: "お客様番号",
      customerGstId: "顧客消費税ID",
      customerPo: "お客様注文番号",
      customer: "顧客",
      customerTaxId: "顧客税ID",
      soldToId: "販売先ID"
    },
    productTable: {
      itemNumber: "品目番号",
      description: "製品名",
      quantity: "数量",
      Price: "価格",
      msrp: "MSRP",
      extendedLineTax: "消費税額",
      extendedCopyrightLevyFee: "著作権使用料",
      lineAmount: "合計",
      unitPrice: "単価",
      tax: "税額"
    },
    summary: {
      subtotal: "小計",
      subtotalWithLevy: "小計",
      gst: "消費税",
      totalTax: "税額合計",
      grandTotal: "総合計",
      downpaymentAmount: "頭金金額",
      remainingAmount: "残額",
      copyrightLevyFee: "著作権使用料"
    },
    payment: {
      paymentTerms: "支払条件",
      paymentMethod: "支払方法",
      bankDetails: "銀行詳細",
      wireTransfer: "銀行振込",
      wireTransferOption: "銀行振込の支払いオプション：",
      bankName: "銀行名：",
      bankAddress: "銀行住所：",
      bankPhone: "銀行電話番号：",
      addressLine1: "住所1：",
      addressLine2: "住所2：",
      addressLine3: "住所3：",
      accountName: "口座名義：",
      accountAddress: "口座住所：",
      accountNumber: "口座番号：",
      ibanNumber: "IBAN番号：",
      swiftCode: "スイフトコード：",
      swiftBicCode: "スイフト/BICコード：",
      switchCode: "スイッチコード：",
      sortCode: "ソートコード：",
      correspondingBank: "コルレス銀行：",
      vat: "TAX：",
      correspondingBankSwiftBicCode: "コルレス銀行スイフト/BICコード：",
      bankKey: "銀行キー：",
      bcCode: "BCコード：",
      currencyCode: "通貨コード：",
      companyCode: "会社コード：",
      remittanceNote: "Microsoftへの送金の際は、迅速な決済のため顧客番号をご参照ください。"
    },
    footer: {
      comments: "コメント",
      paymentAppreciation: "お支払いにつきまして、下記の情報をご利用いただき、迅速なご対応を賜りますようお願い申し上げます。なお、現在は「小切手によるお支払い」はご利用いただけませんので、予めご了承ください。ご入金が確認でき次第、速やかに商品を発送させていただきます。この度は弊社をご利用いただき、誠にありがとうございます。",
      creditCardTitle: "クレジットカード支払いオプション：",
      creditCardInstructions: "クレジットカードでの支払いが希望の支払い方法であることを販売者にお伝えいただいた場合は、提供されたリンクを使用して支払いを完了してください：",
      termsConditions: "利用規約：",
      termsInstructionsWireTransfer: "支払い条件については、購入契約書をご参照ください。受け入れ可能な支払い方法は銀行振込です。支払いは電子資金振替のみで行ってください。この請求書に記載されている住所への物理的な支払いは送付しないでください。支払いが遅れたり不完全な場合は、罰則が科される可能性があります。この請求書についてご質問がある場合は、Retail Stores Invoice サポートまでメールでお問い合わせください：",
      termsInstructionsCreditCard: "支払い条件については、購入契約書をご参照ください。受け入れ可能な支払い方法はクレジットカードです。この請求書に記載されている住所への物理的な支払いは送付しないでください。支払いが遅れたり不完全な場合は、罰則が科される可能性があります。この請求書についてご質問がある場合は、Retail Stores Invoice サポートまでメールでお問い合わせください：",
      supportEmail: "rsinvsup@microsoft.com"
    },
    buttons: {
      downloadPdf: "DPR PDF をダウンロード",
      print: "印刷",
      back: "戻る"
    }
  }
};

// English Labels (Default)
const EnglishLabels: CountryTemplateLabels = {
  quotePreview: {
    quotation: "Quotation",
    microsoft: "Microsoft",
    quoteDetails: {
      quotationId: "Quotation ID",
      effectiveFrom: "Effective From",
      effectiveTo: "Effective To",
      quotationDate: "Quotation Date",
      expiryDate: "Expiry Date"
    },
    customerInfo: {
      companyName: "Company Name",
      accountNumber: "Account Number",
      customerTaxId: "Customer VAT ID",
      contactName: "Contact Name",
      contactEmail: "Contact Email",
      phone: "Phone",
      customerPo: "Customer PO",
      opportunityId: "Opportunity ID"
    },
    microsoftAccount: {
      accountLead: "MSFT Account Lead",
      accountLeadEmail: "Account Lead Email",
      accountLeadPhone: "Account Lead Phone"
    },
    addresses: {
      billTo: "Bill To",
      shipTo: "Ship To"
    },
    productTable: {
      itemNumber: "Item Number",
      description: "Product Name",
      partNumber: "SKU",
      quantity: "Qty",
      Price: "Price",
      discountAmount: "Discount Amount",
      discountPercentage: "Discount Percentage",
      unitPrice: "Unit Price",
      lineTotal: "Line Total",
      tax: "Extended Line Tax",
      fee: "Extended Copyright Levy Fee"
    },
    summary: {
      subtotal: "Subtotal",
      subtotalWithLevy: "Subtotal (including copyright levy)",
      totalDiscount: "Total Discount",
      totalTax: "Tax Amount (Tax Rate)",
      totalFee: "Copyright Levy Total",
      grandTotal: "Total Amount",
      currency: "Currency Code Displayed",
      shipping: "Shipping"
    },
    payment: {
      paymentTerms: "Payment Terms",
      paymentMethod: "Payment Method"
    },
    footer: {
      termsConditions: "Terms & Conditions",
      thankYou: "Thank you for your business.",
      validityNote: "This quotation is valid until the expiry date.",
      contactNote: "If you have any questions, please contact your account representative.",
      customerSignature: "Customer Approval Signature",
      date: "Date",
      termsNote: "Please review the terms of the Quote that are attached. By issuing a Purchase Order or paying the purchase price, you agree to the Business Sales Terms attached hereto for the product.",
      learnMore: "Click here to learn more",
      levyFeeExplanation: "*Levy Fee is a private copying levy (also known as blank media tax or levy). This is an EU government-mandatory scheme in which a special tax or levy (additional to any general sales tax) is charged on purchases of recordable media.",
      copyrightLevyFeeLabel: "Copyright Levy Fee",
      officialNoticeLabel: "Official information notice on private copying",
      reimbursementLabel: "Reimbursement/exemption of copyright levy fee for professional use"
    },
    buttons: {
      generatePdf: "Generate Quote PDF",
      back: "Back",
      print: "Print"
    }
  },
  dprProforma: {
    downpaymentRequest: "Down Payment Request",
    microsoft: "Microsoft",
    dprDetails: {
      pageNumber: "Page #",
      of: "of",
      paymentReference: "Payment Reference",
      dueDate: "Due Date",
      requestNumber: "Request Number",
      requestDate: "Request Created Date",
      quotationNumber: "Quotation ID",
      vatRegistration: "VAT Registration"
    },
    customerInfo: {
      billTo: "Bill To",
      shipTo: "Ship To",
      companyName: "Company Name",
      businessPartner: "Business Partner",
      customerNumber: "Customer Number",
      customerGstId: "Customer GST ID",
      customerPo: "PO Number",
      customer: "Customer",
      customerTaxId: "Customer Tax ID",
      soldToId: "Sold To ID"
    },
    productTable: {
      itemNumber: "Item Number",
      description: "Product Name",
      quantity: "Qty",
      Price: "Price",
      msrp: "MSRP",
      extendedLineTax: "Extended Line Tax",
      extendedCopyrightLevyFee: "Extended Copyright Levy Fee",
      lineAmount: "Line Total",
      unitPrice: "Unit Price",
      tax: "Tax"
    },
    summary: {
      subtotal: "Subtotal",
      subtotalWithLevy: "Subtotal (including copyright levy)",
      gst: "GST",
      totalTax: "Tax Amount (Tax Rate)",
      grandTotal: "Total Amount",
      downpaymentAmount: "Downpayment Amount",
      remainingAmount: "Remaining Amount",
      copyrightLevyFee: "Copyright Levy Fee"
    },
    payment: {
      paymentTerms: "Payment Terms",
      paymentMethod: "Payment Method",
      bankDetails: "Bank Details",
      wireTransfer: "Wire Transfer",
      wireTransferOption: "Payment Option for Wire Transfer:",
      bankName: "Bank Name: ",
      bankAddress: "Bank Address: ",
      bankPhone: "Bank Phone: ",
      addressLine1: "Address Line 1: ",
      addressLine2: "Address Line 2: ",
      addressLine3: "Address Line 3: ",
      accountName: "Account Name: ",
      accountAddress: "Account Address: ",
      accountNumber: "Account Number: ",
      ibanNumber: "IBAN Number: ",
      swiftCode: "Swift Code: ",
      swiftBicCode: "Swift / BIC Code: ",
      switchCode: "Switch Code: ",
      sortCode: "Sort Code: ",
      correspondingBank: "Corresponding Bank: ",
      vat: "VAT: ",
      correspondingBankSwiftBicCode: "Corresponding Bank Swift / BIC Code: ",
      bankKey: "Bank Key: ",
      bcCode: "BC Code: ",
      currencyCode: "Currency Code: ",
      companyCode: "Company Code: ",
      remittanceNote: "When remitting payment to Microsoft, please reference the Customer Number for prompt settlement."
    },
    footer: {
      comments: "Comments",
      paymentAppreciation: " Your timely payment is greatly appreciated using the below payment details. Reminder that Check Payment is no longer an acceptable form of payment. Microsoft will ship the item(s) as soon as we receive your full payment. Thank you for your business.",
      creditCardTitle: "For Credit Card Payment Options:",
      creditCardInstructions: "If you advised your seller that payment by credit card is your preferred payment method, please complete payment using the link provided:",
      termsConditions: "Terms & Conditions:",
      termsInstructionsWireTransfer: "Please refer to your purchase agreement for payment terms. Acceptable payment method is Wire Transfer. Payment should only be made by Electronic Funds Transfer. Do not send any physical payment to any address on this invoice. Late or incomplete payment may result in penalties. For questions about this invoice, please contact Retail Stores Invoice support via email at:",
      termsInstructionsCreditCard: "Please refer to your purchase agreement for payment terms. Acceptable payment method is via Credit Card. Do not send any physical payment to any address on this invoice. Late or incomplete payment may result in penalties. For questions about this invoice, please contact Retail Stores Invoice support via email at:",
      supportEmail: "rsinvsup@microsoft.com"
    },
    buttons: {
      downloadPdf: "Download DPR Proforma PDF",
      print: "Print",
      back: "Back"
    }
  }
};

// French Labels for France
const FranceLabels: CountryTemplateLabels = {
  quotePreview: {
    quotation: "Devis",
    microsoft: "Microsoft",
    quoteDetails: {
      quotationId: "Référence du devis",
      effectiveFrom: "Valable à partir du",
      effectiveTo: "Valable jusqu'au",
      quotationDate: "Date du devis",
      expiryDate: "Date d'expiration"
    },
    customerInfo: {
      companyName: "Nom de la société",
      accountNumber: "Numéro de compte",
      customerTaxId: "Numéro de TVA client VAT ID",
      contactName: "Nom du contact",
      contactEmail: "E-mail de contact",
      phone: "Téléphone",
      customerPo: "Bon de commande client",
      opportunityId: "Référence de l'opportunité"
    },
    microsoftAccount: {
      accountLead: "Responsable de compte MSFT",
      accountLeadEmail: "E-mail du responsable de compte",
      accountLeadPhone: "Téléphone du responsable de compte"
    },
    addresses: {
      billTo: "Adresse de facturation",
      shipTo: "Adresse de livraison"
    },
    productTable: {
      itemNumber: "N° article",
      description: "Nom du produit",
      partNumber: "SKU",
      quantity: "Qté",
      Price: "Prix",
      discountAmount: "Montant de la remise",
      discountPercentage: "Pourcentage de remise",
      unitPrice: "Prix unitaire",
      lineTotal: "Total ligne",
      tax: "TVA Sur La Ligne Étendue",
      fee: "Frais De Redevance Pour Copie Privée Étendus"
    },
    summary: {
      subtotal: "Sous-total",
      subtotalWithLevy: "Sous-total (incluant la redevance pour copie privée)",
      totalDiscount: "Remise totale",
      totalTax: "Montant TVA (Taux TVA)",
      totalFee: "Total de la redevance pour copie privée",
      grandTotal: "Montant total",
      currency: "Code devise affiché",
      shipping: "Expédition"
    },
    payment: {
      paymentTerms: "Conditions de paiement",
      paymentMethod: "Mode de paiement"
    },
    footer: {
      termsConditions: "Conditions générales",
      thankYou: "Merci pour votre confiance.",
      validityNote: "Ce devis est valable jusqu'à la date d'expiration.",
      contactNote: "Pour toute question, veuillez contacter votre responsable de compte.",
      customerSignature: "Signature d'approbation du client",
      date: "Date",
      termsNote: "Veuillez prendre connaissance des conditions du devis ci-joint. En émettant un bon de commande ou en payant le prix d'achat, vous acceptez les Conditions de vente pour les entreprises ci-jointes pour le produit en question.",
      learnMore: "Cliquez ici pour en savoir plus",
      levyFeeExplanation: "*La redevance pour copie privée est une taxe sur les supports d'enregistrement. Il s'agit d'un régime obligatoire du gouvernement de l'UE dans lequel une taxe ou une redevance spéciale (en plus de toute taxe de vente générale) est facturée sur les achats de supports d'enregistrement.",
      copyrightLevyFeeLabel: "Redevance pour copie privée",
      officialNoticeLabel: "Avis d'information officiel sur la copie privée",
      reimbursementLabel: "Remboursement/exonération de la redevance pour copie privée pour usage professionnel"
    },
    buttons: {
      generatePdf: "Générer le devis en PDF",
      back: "Retour",
      print: "Imprimer"
    }
  },
  dprProforma: {
    downpaymentRequest: "Facture proforma",
    microsoft: "Microsoft",
    dprDetails: {
      pageNumber: "Page",
      of: "sur",
      paymentReference: "Référence de paiement",
      dueDate: "Date d'échéance",
      requestNumber: "Numéro de demande",
      requestDate: "Date de création de la demande",
      quotationNumber: "Référence du devis",
      vatRegistration: "Immatriculation TVA"
    },
    customerInfo: {
      billTo: "Adresse de facturation",
      shipTo: "Adresse de livraison",
      companyName: "Nom de la société",
      businessPartner: "Partenaire commercial",
      customerNumber: "Numéro de compte",
      customerGstId: "N° de TVA du client",
      customerPo: "N° de bon de commande",
      customer: "Client",
      customerTaxId: "N° de TVA du client",
      soldToId: "Référence du destinataire"
    },
    productTable: {
      itemNumber: "N° article",
      description: "Nom du produit",
      quantity: "Qté",
      Price: "Prix",
      msrp: "PDSF",
      extendedLineTax: "TVA Sur La Ligne Étendue",
      extendedCopyrightLevyFee: "Frais De Redevance Pour Copie Privée Étendus",
      lineAmount: "Total Ligne",
      unitPrice: "Prix unitaire",
      tax: "TVA"
    },
    summary: {
      subtotal: "Sous-total",
      subtotalWithLevy: "Sous-total (incluant la redevance pour copie privée)",
      gst: "TVA",
      totalTax: "Montant TVA (Taux TVA)",
      grandTotal: "Montant total",
      downpaymentAmount: "Montant de l'acompte",
      remainingAmount: "Montant restant",
      copyrightLevyFee: "Frais de redevance pour copie privée"
    },
    payment: {
      paymentTerms: "Conditions de paiement",
      paymentMethod: "Mode de paiement",
      bankDetails: "Coordonnées bancaires",
      wireTransfer: "Virement bancaire",
      wireTransferOption: "Option de paiement par virement bancaire: ",
      bankName: "Nom de la banque: ",
      bankAddress: "Adresse de la banque: ",
      bankPhone: "Téléphone de la banque: ",
      addressLine1: "Adresse ligne 1",
      addressLine2: "Adresse ligne 2",
      addressLine3: "Adresse ligne 3",
      accountName: "Nom du titulaire du compte: ",
      accountAddress: "Adresse du titulaire du compte: ",
      accountNumber: "Numéro de compte: ",
      ibanNumber: "Numéro IBAN: ",
      swiftCode: "Code Swift: ",
      swiftBicCode: "Code Swift / BIC de la banque correspondante: ",
      switchCode: "Code Switch",
      sortCode: "Code de tri",
      correspondingBank: "Banque correspondante",
      vat: "TVA: ",
      correspondingBankSwiftBicCode: "Code Swift / BIC de la banque correspondante: ",
      bankKey: "Clé bancaire",
      bcCode: "Code BC",
      currencyCode: "Code devise",
      companyCode: "Code société",
      remittanceNote: "Lors du paiement à Microsoft, veuillez indiquer le numéro client pour un règlement rapide."
    },
    footer: {
      comments: "Commentaires",
      paymentAppreciation: "Nous vous remercions d'effectuer votre paiement dès maintenant en utilisant les coordonnées ci-dessous. Veuillez noter que le paiement par chèque n'est plus accepté. Microsoft expédiera les articles dès réception de votre paiement complet. Merci pour votre confiance.",
      creditCardTitle: "Pour les options de paiement par carte de crédit:",
      creditCardInstructions: "Si vous avez informé votre vendeur que le paiement par carte de crédit est votre méthode de paiement préférée, veuillez effectuer le paiement en utilisant le lien fourni.",
      termsConditions: "Termes et conditions:",
      termsInstructionsWireTransfer: "Veuillez vous référer à votre contrat d'achat pour les modalités de paiement. Le paiement accepté est par virement bancaire. Le paiement doit être effectué uniquement par transfert électronique de fonds. N'envoyez aucun paiement physique à l'adresse figurant sur cette facture. Tout retard ou paiement incomplet peut entraîner des pénalités. Pour toute question concernant cette facture, contactez le support facturation Retail Stores par e-mail :",
      termsInstructionsCreditCard: "Veuillez vous référer à votre contrat d'achat pour les modalités de paiement. Le paiement accepté est par carte bancaire. N'envoyez aucun paiement physique à l'adresse figurant sur cette facture. Tout retard ou paiement incomplet peut entraîner des pénalités. Pour toute question concernant cette facture, contactez le support facturation Retail Stores par e-mail :",
      supportEmail: "rsinvsup@microsoft.com"
    },
    buttons: {
      downloadPdf: "Télécharger le PDF de la proforma DPR",
      print: "Imprimer",
      back: "Retour"
    }
  }
};

// Export country labels collection
export const QuoteTemplateLabels: Record<string, CountryTemplateLabels> = {
  Japan: JapanLabels,
  France: FranceLabels,
  English: EnglishLabels,
  Default: EnglishLabels,
};

/**
 * Get template labels for a country with language support
 * @param country - The country code
 * @param language - Optional language code
 * @returns CountryTemplateLabels for the specified country/language
 */
export const getQuoteTemplateLabels = (country: string, language?: string): CountryTemplateLabels => {
  // For France, support language switching between French and English
  if (country === 'France') {
    if (language && (language.toLowerCase() === 'en' || language.toLowerCase() === 'english')) {
      return EnglishLabels; // Use standard English labels for France English
    }
    // Default to French for France
    return FranceLabels;
  }
  
  // For Japan, support language switching between Japanese and English
  if (country === 'Japan') {
    if (language && (language.toLowerCase() === 'en' || language.toLowerCase() === 'english')) {
      return EnglishLabels; // Use standard English labels for Japan English
    }
    // Default to Japanese for Japan
    return JapanLabels;
  }
  
  return QuoteTemplateLabels[country] || QuoteTemplateLabels.Default;
};

export const getQuotePreviewLabels = (country: string, language?: string): QuotePreviewLabels => {
  return getQuoteTemplateLabels(country, language).quotePreview;
};

export const getDPRProformaLabels = (country: string, language?: string): DPRProformaLabels => {
  return getQuoteTemplateLabels(country, language).dprProforma;
};

// Get default language for a country
export const getDefaultLanguage = (country: string): string => {
  if (country === 'France') return 'fr';
  if (country === 'Japan') return 'ja';
  return 'en';
};

// Get available languages for a country
export const getAvailableLanguages = (country: string): Array<{ code: string; label: string }> => {
  if (country === 'France') {
    return [
      { code: 'fr', label: 'Français' },
      { code: 'en', label: 'English' }
    ];
  }
  if (country === 'Japan') {
    return [
      { code: 'ja', label: '日本語' },
      { code: 'en', label: 'English' }
    ];
  }
  return [{ code: 'en', label: 'English' }];
};
